import React, { useMemo } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { HtmlTooltip } from "../../../component/Methods";
import { formatTimeAgo } from "../../../utils/helpers";
import { NO_EXTRA_BENCHMARKS } from "../../../utils/portfolioBenchmarks";
import { v2CardBg, v2CardBorder, v2LabelSx } from "../v2Theme";

const BENCHMARKS = [
  { name: "NASDAQ", symbol2: "qqq" },
  { name: "S&P 500", symbol2: "spy" },
  { name: "RUSSELL 2000", symbol2: "iwm" },
  { name: "DOW JONES", symbol2: "dia" },
  { name: "RUSSELL 3000", symbol2: "iyk" },
];

const BenchmarkTicker = ({
  marketData,
  extraBenchmarks = NO_EXTRA_BENCHMARKS,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const extras = Array.isArray(extraBenchmarks)
    ? extraBenchmarks
    : NO_EXTRA_BENCHMARKS;

  const rows = useMemo(() => {
    return [...BENCHMARKS, ...extras].map((benchmark) => {
      const matchingData2 = marketData?.find(
        (data) =>
          String(data?.symbol || "").toLowerCase() ===
          String(benchmark.symbol2 || "").toLowerCase()
      );
      const toReturn = {
        name: benchmark.name,
        symbol2: benchmark.symbol2,
        val2: 0,
        date2: "N/A",
      };

      if (matchingData2) {
        const { price, yestPrice, expiration } = matchingData2;
        const chPer = (((price - yestPrice) / yestPrice) * 100).toFixed(2);
        toReturn.val2 = chPer;
        toReturn.date2 = expiration;
      }
      return toReturn;
    });
  }, [marketData, extras]);

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 1,
        overflowX: "auto",
        py: 1.1,
        px: 1.25,
        borderRadius: 2,
        border: "1px solid",
        borderColor: v2CardBorder(theme),
        backgroundColor: v2CardBg(theme),
        color: "text.primary",
        scrollbarWidth: "thin",
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          flexShrink: 0,
          mr: 0.5,
          fontSize: "0.85rem",
          ...v2LabelSx(theme),
        }}
      >
        Benchmarks
      </Typography>

      {rows.map((row) => {
        const val = Number(row.val2) || 0;
        const positive = val >= 0;
        const tint = positive
          ? alpha(theme.palette.success.main, isDark ? 0.2 : 0.12)
          : alpha(theme.palette.error.main, isDark ? 0.2 : 0.12);
        const color =
          theme.palette.mode === "dark"
            ? positive
              ? "#4ade80"
              : "#f87171"
            : positive
              ? "#16a34a"
              : "#dc2626";

        return (
          <HtmlTooltip
            key={row.symbol2}
            title={
              <React.Fragment>
                <Typography color="inherit" sx={{ fontSize: "12px" }}>
                  {row.name}
                </Typography>
                <Typography color="inherit" sx={{ fontSize: "12px" }}>
                  Updated: <b>{formatTimeAgo(row?.date2).msg}</b>
                </Typography>
              </React.Fragment>
            }
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                px: 1.25,
                py: 0.65,
                borderRadius: 1.5,
                backgroundColor: tint,
                border: "1px solid",
                borderColor: alpha(color, isDark ? 0.4 : 0.25),
                flexShrink: 0,
                cursor: "default",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  color: "text.primary",
                  fontSize: "0.85rem",
                }}
              >
                {row.symbol2}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  fontVariantNumeric: "tabular-nums",
                  color,
                  fontSize: "0.85rem",
                }}
              >
                {positive ? `${val}%` : `(${Math.abs(val)}%)`}
              </Typography>
            </Box>
          </HtmlTooltip>
        );
      })}
    </Box>
  );
};

export default React.memo(BenchmarkTicker);
